/* eslint react-hooks/exhaustive-deps: error */
import * as React from 'react';
import {
  TypeImplementation,
  Result,
  Plugins,
  getMeta,
} from '@packages/systems/wfdl';
import {entries} from '@packages/utilities/key-map';
import type {MetaAttributes} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  ContextualExpressions,
  stripAnn,
  getAnn,
  type ContextualExpression,
} from '@packages/utilities/contextual-expression';
import {ELiteral} from '@packages/systems/wfdl-ast';
import {getString} from '@packages/utilities/json';
import type {Json} from '@packages/utilities/json';
import {
  updateExpressionAction,
  useExpressionEditorImplementationContext,
} from '@packages/systems/ExpressionEditor-utils';
import {useDesignerSelector} from '@packages/systems/designer/flux';
import {INHERIT_VARIABLE_MODE_ID} from '@packages/systems/core/utils/StyleVariables';
import type {DesignerStores} from '@packages/systems/designer/flux';
import {
  getVariableModeCollectionId,
  getVariableModeMeta,
  VariableModeSelect,
} from '@packages/systems/variable-mode-props';
import {
  ChangeMetaType,
  Checkbox,
  Switch,
  Text,
  TextInput,
  VStack,
} from '@packages/systems/spring';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';

const selectVariableCollections = (state: DesignerStores) =>
  state.CssVariablesStore.variableCollections;

const VariableModeMetaUI = ({
  meta,
  onChange,
}: {
  meta: MetaAttributes | null;
  onChange: (arg1: MetaAttributes) => void;
}) => {
  const isCampaignBuildMode = useFeatureFlag(FEATURE_FLAGS.CAMPAIGN_BUILD_MODE);
  const variableCollections = useDesignerSelector(selectVariableCollections);
  const currentMeta = getVariableModeMeta(meta);
  const collectionId = currentMeta?.collectionId;
  const collection = collectionId
    ? (variableCollections.get(collectionId) ?? null)
    : null;

  const showInherit = currentMeta?.showInherit ?? true;
  const baseDisplayName = currentMeta?.baseDisplayName ?? '';
  const visibleModeIds = currentMeta?.visibleModeIds;

  const handleShowInheritChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({...currentMeta, showInherit: e.target.checked});
    },
    [currentMeta, onChange]
  );

  const [localBaseDisplayName, setLocalBaseDisplayName] =
    React.useState(baseDisplayName);

  React.useEffect(() => {
    setLocalBaseDisplayName(baseDisplayName);
  }, [baseDisplayName]);

  const handleBaseDisplayNameInput = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalBaseDisplayName(e.target.value);
    },
    []
  );

  const handleBaseDisplayNameBlur = React.useCallback(() => {
    onChange({
      ...currentMeta,
      baseDisplayName: localBaseDisplayName || undefined,
    });
  }, [currentMeta, onChange, localBaseDisplayName]);

  const handleModeVisibilityChange = React.useCallback(
    (modeId: string, e: React.ChangeEvent<HTMLInputElement>) => {
      if (!collection) return;
      const checked = e.target.checked;

      const allModeIds = entries(collection.modes)
        .filter(([_, {deleted}]) => !deleted)
        .map(([id]) => id);

      let nextVisibleIds: string[] | undefined;
      if (checked) {
        if (visibleModeIds) {
          nextVisibleIds = [...visibleModeIds, modeId];
          if (nextVisibleIds.length === allModeIds.length) {
            nextVisibleIds = undefined;
          }
        }
      } else {
        const current = visibleModeIds ?? allModeIds;
        nextVisibleIds = current.filter((id) => id !== modeId);
      }

      onChange({...currentMeta, visibleModeIds: nextVisibleIds});
    },
    [collection, currentMeta, onChange, visibleModeIds]
  );

  if (!isCampaignBuildMode || !collection) return null;

  const allModes = entries(collection.modes).filter(
    ([_, {deleted}]) => !deleted
  );

  return (
    <>
      <Text colorVariant="secondary">Show inherit</Text>
      <Switch
        isChecked={showInherit}
        onChange={handleShowInheritChange}
        aria-label="Show inherit option"
      />
      <Text colorVariant="secondary">Base name</Text>
      <TextInput
        value={localBaseDisplayName}
        onChange={handleBaseDisplayNameInput}
        onBlur={handleBaseDisplayNameBlur}
        placeholder="Base"
        aria-label="Base mode display name"
      />
      {allModes.length > 0 && (
        <>
          <Text colorVariant="secondary">Visible modes</Text>
          <VStack>
            {allModes.map(([modeId, {name}]) => {
              const isVisible = visibleModeIds
                ? visibleModeIds.includes(modeId)
                : true;
              return (
                <Checkbox
                  key={modeId}
                  isChecked={isVisible}
                  onChange={(e) => handleModeVisibilityChange(modeId, e)}
                  label={name}
                />
              );
            })}
          </VStack>
        </>
      )}
    </>
  );
};

const VariableModeRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: TypeImplementation.ExpressionEditorDispatch;
}) => {
  const {env} = useExpressionEditorImplementationContext();
  const variableCollections = useDesignerSelector(selectVariableCollections);

  const exprAnn = getAnn(expr);
  const type = exprAnn.type;
  const collectionId = type ? getVariableModeCollectionId(type) : null;
  const collection = collectionId
    ? (variableCollections.get(collectionId) ?? null)
    : null;

  const meta = type ? getVariableModeMeta(getMeta(type)) : null;

  const literal = ContextualExpressions.getLiteral(stripAnn(expr));
  const value =
    (literal ? getString(literal.value) : undefined) ??
    INHERIT_VARIABLE_MODE_ID;

  const handleChange = React.useCallback(
    (modeId: string) => {
      dispatch(
        updateExpressionAction(
          ELiteral({
            name: qualify(Plugins.name('Basic'), typeName('VariableMode')),
            value: modeId,
          })
        )
      );
    },
    [dispatch]
  );

  const handlePreselect = React.useCallback(
    (nextValue: string, {changeType}: {changeType: ChangeMetaType}) => {
      if (changeType === 'changeCancel') {
        env.transaction.abort();
        return;
      }

      if (changeType === 'changeStart') {
        env.transaction.begin();
        return;
      }

      if (changeType === 'change' && nextValue != null) {
        handleChange(nextValue);
      }
    },
    [env.transaction, handleChange]
  );

  const handleCommit = React.useCallback(
    (nextValue: string, {changeType}: {changeType: ChangeMetaType}) => {
      if (changeType !== 'changeEnd') {
        return;
      }

      if (nextValue != null) {
        handleChange(nextValue);
      }

      env.transaction.commit();
    },
    [env.transaction, handleChange]
  );

  const handleUnlink = React.useCallback(() => {
    handleChange(INHERIT_VARIABLE_MODE_ID);
  }, [handleChange]);

  if (!collection) return null;

  return (
    <VariableModeSelect
      collection={collection}
      value={value}
      onChange={handleCommit}
      onPreselect={handlePreselect}
      onUnlink={handleUnlink}
      isDisabled={env.uiFlags?.disabled?.expression?.(expr)}
      showInherit={meta?.showInherit}
      baseDisplayName={meta?.baseDisplayName}
      visibleModeIds={meta?.visibleModeIds}
    />
  );
};

const VariableMode = TypeImplementation.Custom({
  params: ['collectionId'],
  parseValue: (json: Json) => {
    return typeof json === 'string'
      ? Result.Ok(json)
      : Result.Error('Expected a string for VariableMode');
  },
  render: VariableModeRender,
  metaUI: VariableModeMetaUI,
  icon: TypeImplementation.IconByName(
    qualify(Plugins.name('Basic'), iconName('VariableMode'))
  ),
  system: {
    displayName: 'Variable Mode',
    label: 'Variable Mode',
  },
});

export default VariableMode;
